import type { HookOptions } from './hook-builders/types';

import { useCallback, useMemo } from 'react';

import { useAllSpaceConstraints } from './useAllSpaceConstraints';

const DEFAULT_OPTIONS = {};

interface HookSpaceConstraintObjectResult {
  constraint: number | string;
  isLoading: boolean;
  errors: any;
  reload: () => void;
}

type HookSpaceConstraintArrayResult = [
  defaultSpaceSlug: string | null,
  isLoading: boolean,
  errors: any,
  reload: () => void,
];

type HookSpaceConstraintResult = HookSpaceConstraintObjectResult &
  HookSpaceConstraintArrayResult;

export function useSpaceConstraint(
  name: string,
  space?: string,
  options: HookOptions = DEFAULT_OPTIONS,
) {
  const {
    allConstraints,
    isLoading,
    reload: reloadAllConstraints,
    errors,
  } = useAllSpaceConstraints(space, options);

  const constraint = useMemo(
    () =>
      allConstraints && name in allConstraints
        ? allConstraints?.[name as keyof typeof allConstraints]
        : undefined,
    [allConstraints, name],
  );

  const reload = useCallback(async () => {
    return reloadAllConstraints([name]);
  }, [reloadAllConstraints, name]);

  const result = [constraint, isLoading, errors, reload];
  Object.assign(result, {
    constraint,
    isLoading,
    errors,
    reload,
  });
  return result as HookSpaceConstraintResult;
}
